import { CONTEXT_SAMPLE_RATE } from '@core/audio/audio-constants';
import { Resampler } from '@core/audio/resampler';
import { SOURCE_TRANSLATED_VOICE, decodeFrame } from '@core/audio/pcm-frame';
import {
  TRANSLATION_SAMPLE_RATE,
  decodeServerMessage,
  type StartMessage,
} from '@core/translation/translation-protocol';
import type {
  PlayoutStats,
  VoiceEngine,
  VoiceEngineOptions,
  VoiceEngineSession,
} from './voice-engine';

export interface ServerVoiceEngineConfig {
  /** The translation server's socket — the same endpoint captions use. */
  endpoint: string;
  /** Read lazily, as for captions: no session id exists at bootstrap. */
  sessionId?: () => string | null;
}

const TAP_WORKLET_URL = 'assets/worklets/translation-tap-processor.js';
const TAP_PROCESSOR = 'translation-tap-processor';
const PLAYOUT_WORKLET_URL = 'assets/worklets/voice-playout-processor.js';
const PLAYOUT_PROCESSOR = 'voice-playout-processor';

/**
 * Same reasoning as captions: an upgrade a proxy drops never says so. Longer
 * than the caption timeout, because `ready` waits on the model's handshake too.
 */
const READY_TIMEOUT_MS = 10_000;

/**
 * When the translation of what was said before a mute counts as finished
 * (`whenDrained`). Tuned to the model, which answers 2–4 s after speech:
 *
 *   QUIET      translated audio has stopped arriving for this long, after the
 *              last speech, and nothing is left to play;
 *   UNANSWERED no audio at all has come back this long after the last speech —
 *              the model dropped it (a cough, room noise);
 *   MAX        never hold the translated track open longer than this.
 */
const DRAIN_QUIET_MS = 2_500;
const DRAIN_UNANSWERED_MS = 8_000;
const DRAIN_MAX_MS = 30_000;
const DRAIN_POLL_MS = 100;

/**
 * Voice translation through our translation server (§8.9).
 *
 * The browser still recognises nothing. It sends the microphone up as 16 kHz
 * PCM, exactly as the caption tap does, and plays whatever audio comes back.
 * Which model runs, and what format it wants, is the server's business — the
 * downlink frames declare their own sample rate, so a model returning 24 kHz or
 * 16 kHz needs no change here.
 */
export class ServerVoiceEngine implements VoiceEngine {
  readonly name = 'server';

  constructor(private readonly config: ServerVoiceEngineConfig) {}

  async start(input: MediaStream, options: VoiceEngineOptions): Promise<VoiceEngineSession> {
    const session = new ServerVoiceSession(input, options);
    try {
      await session.open(this.config.endpoint, {
        type: 'start',
        output: 'voice',
        sourceLanguage: options.sourceLanguage,
        targetLanguage: options.targetLanguage,
        sampleRate: TRANSLATION_SAMPLE_RATE,
        sessionId:
          (typeof this.config.sessionId === 'function' && this.config.sessionId()) ||
          'unknown',
      });
    } catch (error) {
      // Nothing half-built may outlive a failed start: the socket and the
      // context each hold resources the §10A.3 assertion would report.
      await session.stop();
      throw error;
    }
    return session;
  }
}

/**
 * One socket, one audio context, one side of the consultation.
 *
 * The context is created first and closed last. Its destination's stream is
 * `output`, so a failure tears down the socket and the tap but leaves the
 * context running: the published track keeps carrying silence until the caller
 * has swapped the microphone back in (§8.9).
 */
class ServerVoiceSession implements VoiceEngineSession {
  readonly output: MediaStream;

  private readonly context = new AudioContext({ sampleRate: CONTEXT_SAMPLE_RATE });
  private readonly destination = this.context.createMediaStreamDestination();
  private readonly resampler = new Resampler();
  private downlinkRate = 0;

  private socket: WebSocket | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private tap: AudioWorkletNode | null = null;
  private sink: GainNode | null = null;
  private playout: AudioWorkletNode | null = null;

  private failed = false;
  private stopped = false;

  // What `whenDrained` decides from. Times are `performance.now()`; 0 is never.
  private lastSpeechAt = 0;
  private lastAudioAt = 0;
  /** Samples posted to the playout worklet, against those it has let go of. */
  private samplesPosted = 0;
  private samplesConsumed = 0;

  constructor(
    private readonly input: MediaStream,
    private readonly options: VoiceEngineOptions,
  ) {
    this.output = this.destination.stream;
  }

  async open(endpoint: string, start: StartMessage): Promise<void> {
    // Join is behind a tap, so resuming here satisfies autoplay in Chrome and
    // the WebView; a suspended context runs no worklet and produces nothing.
    if (this.context.state === 'suspended') await this.context.resume();

    await this.context.audioWorklet.addModule(TAP_WORKLET_URL);
    await this.context.audioWorklet.addModule(PLAYOUT_WORKLET_URL);

    this.playout = new AudioWorkletNode(this.context, PLAYOUT_PROCESSOR, {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    this.playout.port.onmessage = (event) => this.onPlayoutMessage(event.data);
    this.playout.connect(this.destination);

    const socket = new WebSocket(endpoint);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    await waitForOpen(socket);
    socket.send(JSON.stringify(start));
    await waitForReady(socket);

    this.startTap(socket);

    socket.onmessage = (event) => this.onMessage(event);
    socket.onerror = () => this.fail('the translation service is unreachable');
    socket.onclose = (event) => {
      if (this.stopped || event.wasClean) return;
      this.fail('the translation service disconnected');
    };
  }

  async stop(): Promise<void> {
    if (this.stopped) return;
    this.stopped = true;

    this.releaseUplink();

    this.playout?.port.close();
    this.playout?.disconnect();
    this.playout = null;

    // Ended explicitly: closing the context does not end its destination's
    // track synchronously, and a wrapper checking `readyState` must see it.
    this.output.getTracks().forEach((track) => track.stop());

    try {
      await this.context.close();
    } catch {
      // Already closed; the graph is unreachable either way.
    }
  }

  async whenDrained(signal: AbortSignal): Promise<void> {
    const started = performance.now();
    while (!signal.aborted && !this.stopped && !this.failed) {
      const now = performance.now();
      if (this.isDrained(now) || now - started >= DRAIN_MAX_MS) return;
      await abortableDelay(DRAIN_POLL_MS, signal);
    }
  }

  /**
   * Decided from what was actually sent, received and played — not from the
   * activity phase, which is an indicator and lapses during the model's pauses
   * between phrases while it still owes the rest of the sentence.
   */
  private isDrained(now: number): boolean {
    if (this.samplesPosted > this.samplesConsumed) return false;
    if (this.lastSpeechAt === 0) return true;

    const answered = this.lastAudioAt > this.lastSpeechAt;
    return answered
      ? now - this.lastAudioAt >= DRAIN_QUIET_MS
      : now - this.lastSpeechAt >= DRAIN_UNANSWERED_MS;
  }

  private onPlayoutMessage(data: unknown): void {
    const message = data as { type?: string; consumed?: number } & Partial<PlayoutStats>;
    if (typeof message?.consumed === 'number') {
      this.samplesConsumed = Math.max(this.samplesConsumed, message.consumed);
    }
    if (message?.type === 'stats') {
      this.options.onPlayout?.({
        buffered: message.buffered ?? 0,
        drains: message.drains ?? 0,
        overruns: message.overruns ?? 0,
      });
    }
  }

  /**
   * The same tap as captions: 16 kHz int16 frames, posted from the audio
   * thread. Pulled through a zero-gain sink, because Chromium does not reliably
   * run a node with no path to the destination.
   */
  private startTap(socket: WebSocket): void {
    this.source = this.context.createMediaStreamSource(this.input);
    this.tap = new AudioWorkletNode(this.context, TAP_PROCESSOR, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });

    this.tap.port.onmessage = (event) => {
      const data = event.data;
      if (data?.type !== 'frame') return;
      if (this.failed || socket.readyState !== WebSocket.OPEN) return;
      const speech = isSpeech(data.buffer as ArrayBuffer);
      socket.send(data.buffer as ArrayBuffer);
      if (speech) {
        this.lastSpeechAt = performance.now();
        this.options.onSpeech?.();
      }
    };

    this.source.connect(this.tap);
    this.sink = this.context.createGain();
    this.sink.gain.value = 0;
    this.tap.connect(this.sink);
    this.sink.connect(this.context.destination);
  }

  private onMessage(event: MessageEvent): void {
    if (event.data instanceof ArrayBuffer) {
      this.play(event.data);
      return;
    }
    if (typeof event.data !== 'string') return;

    const result = decodeServerMessage(event.data);
    if (!result.ok) return;

    if (result.message.type === 'error') {
      this.fail(result.message.message);
    } else if (result.message.type === 'segment') {
      const { type: _type, ...segment } = result.message;
      this.options.onText?.(segment);
    }
  }

  /** Decode one downlink frame, bring it to the context rate, and queue it. */
  private play(buffer: ArrayBuffer): void {
    const result = decodeFrame(buffer);
    // A malformed frame is dropped rather than fatal, as for captions: one bad
    // message is a bug to find in the logs, not a reason to cut the voice.
    if (!result.ok || result.frame.sourceId !== SOURCE_TRANSLATED_VOICE) return;

    const { frame } = result;
    if (frame.sampleRate !== this.downlinkRate) {
      this.downlinkRate = frame.sampleRate;
      this.resampler.configure(frame.sampleRate, this.context.sampleRate);
    }

    const samples = this.resampler.process(frame.samples);
    if (samples.length === 0 || !this.playout) return;
    this.lastAudioAt = performance.now();
    this.samplesPosted += samples.length;
    this.playout.port.postMessage({ type: 'pcm', samples }, [samples.buffer]);
    this.options.onAudio?.();
  }

  /** Report once and stop sending. The output stays live — see the class note. */
  private fail(reason: string): void {
    if (this.failed || this.stopped) return;
    this.failed = true;
    this.releaseUplink();
    this.options.onError(reason);
  }

  private releaseUplink(): void {
    this.tap?.port.close();
    this.tap?.disconnect();
    this.tap = null;
    this.sink?.disconnect();
    this.sink = null;
    this.source?.disconnect();
    this.source = null;

    const socket = this.socket;
    this.socket = null;
    if (!socket) return;

    if (socket.readyState === WebSocket.OPEN) {
      try {
        // Lets the server flush the last sentence rather than dropping it.
        socket.send(JSON.stringify({ type: 'stop' }));
      } catch {
        // Closed between the check and the send; the close below is what matters.
      }
    }
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    socket.close();
  }
}

/**
 * RMS above which an int16 frame counts as speech: about -36 dBFS. The input
 * is the call's echo-cancelled microphone, so room noise sits well below it
 * and the far side's voice from the speaker is mostly removed already.
 */
const SPEECH_RMS = 520;

/** Resolves after `ms`, or as soon as `signal` aborts. Never rejects. */
function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      window.clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = window.setTimeout(done, ms);
    signal.addEventListener('abort', done, { once: true });
  });
}

/** Whether one 20 ms uplink frame is loud enough to be someone talking. */
function isSpeech(buffer: ArrayBuffer): boolean {
  const samples = new Int16Array(buffer);
  if (samples.length === 0) return false;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return Math.sqrt(sum / samples.length) > SPEECH_RMS;
}

function waitForOpen(socket: WebSocket): Promise<void> {
  if (socket.readyState === WebSocket.OPEN) return Promise.resolve();
  return new Promise((resolve, reject) => {
    socket.onopen = () => resolve();
    socket.onerror = () =>
      reject(
        new Error(
          `could not reach the translation service at ${socket.url} — check that ` +
            'it is running, and that the URL is wss:// when the page is served over HTTPS',
        ),
      );
  });
}

function waitForReady(socket: WebSocket): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error('the translation service did not respond')),
      READY_TIMEOUT_MS,
    );

    socket.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      const result = decodeServerMessage(event.data);

      if (!result.ok) {
        window.clearTimeout(timer);
        reject(new Error(`malformed handshake: ${result.reason}`));
      } else if (result.message.type === 'error') {
        window.clearTimeout(timer);
        reject(new Error(result.message.message));
      } else if (result.message.type === 'ready') {
        window.clearTimeout(timer);
        if (result.message.capabilities.emitsAudio) {
          resolve();
        } else {
          // A caption-only engine would accept the session and send no audio:
          // the far side would hear silence with nothing to explain it.
          reject(new Error(`${result.message.capabilities.engine} does not produce voice`));
        }
      }
    };
  });
}
